import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { concat } from '@ember/helper';
import { modifier } from 'ember-modifier';
import style from 'ember-style-modifier/modifiers/style';
import {
  setupCanvas,
  drawGridLines,
  drawYAxisLabels,
  drawXAxisLabels,
} from '../../utils/charts/canvas-utils.ts';
import { linearScale, niceAxisTicks } from '../../utils/charts/chart-scales.ts';
import {
  computeLayout,
  formatAxisValue,
  formatNumber,
  topTooltipRows,
} from '../../utils/charts/chart-layout.ts';
import { getThemeColors } from '../../utils/charts/color-palette.ts';
import clampTooltip from '../../modifiers/clamp-tooltip.ts';
import { createDebouncedResizeObserver } from '../../utils/charts/resize-observer-utils.ts';

// MultiLineChart – Canvas-rendered chart with multiple named series,
// optional fill/dashed per series, and optional dual y-axes.

/**
 * A single data series for MultiLineChart.
 */
export interface MultiLineSeries {
  /** Display name shown in legend and tooltip */
  label: string;
  /** Y values — one per x position, aligned to the parent `labels` array */
  data: number[];
  /** Line and fill color (hex or CSS color) */
  color: string;
  /** Render as a dashed line (default false) */
  dashed?: boolean;
  /** Fill the area below the line with a semi-transparent wash (default false) */
  filled?: boolean;
  /** Which y-axis this series is scaled against (default 'left') */
  yAxis?: 'left' | 'right';
  /**
   * When true the series is excluded from canvas drawing and the legend,
   * but its values still appear in the hover tooltip.
   */
  hidden?: boolean;
  /**
   * When true the tooltip row for this series is suppressed whenever its
   * value at the hovered index is zero. Useful for optional breakdown rows
   * (e.g. employer payment or subsidy) that are absent in many months.
   */
  hideWhenZero?: boolean;
}

interface MultiLineChartSignature {
  Args: {
    /** X-axis labels — one per data point */
    labels: string[];
    /** Named series to overlay on the same chart */
    series: MultiLineSeries[];
    /** Fixed chart height in pixels (default 260) */
    height?: number;
    /** Formatter for left y-axis tick labels and tooltip values (default: compact number) */
    formatValue?: (v: number) => string;
    /** Formatter for right y-axis tick labels and tooltip values (default: same as formatValue) */
    formatValueRight?: (v: number) => string;
    /**
     * Floor for the left y-axis maximum. When set, the axis is scaled to at
     * least this value (still expanding if the data exceeds it) so multiple
     * charts can share an identical y-scale for visual comparison.
     */
    yMax?: number;
    /** Floor for the right y-axis maximum (see {@link yMax}). */
    yMaxRight?: number;
  };
  Element: HTMLDivElement;
}

const DEFAULT_HEIGHT = 260;

export default class MultiLineChart extends Component<MultiLineChartSignature> {
  @tracked containerWidth = 400;
  @tracked hoveredIndex: number | null = null;
  @tracked tooltipX = 0;
  @tracked tooltipY = 0;

  // ── Computed getters ─────────────────────────────────────────────────────

  get height() {
    return this.args.height ?? DEFAULT_HEIGHT;
  }

  get labels() {
    return this.args.labels ?? [];
  }

  get series() {
    return this.args.series ?? [];
  }

  get hasRightAxis() {
    return this.series.some((s) => s.yAxis === 'right');
  }

  get rightMargin() {
    return this.hasRightAxis ? 65 : 20;
  }

  get tooltipLabel() {
    if (this.hoveredIndex === null) return '';
    return this.labels[this.hoveredIndex] ?? '';
  }

  @cached
  private get cappedTooltip() {
    if (this.hoveredIndex === null) return { rows: [], moreCount: 0 };
    const idx = this.hoveredIndex;
    const fmtLeft = this.args.formatValue ?? formatNumber;
    const fmtRight = this.args.formatValueRight ?? fmtLeft;
    return topTooltipRows(
      this.series
        .filter((s) => idx < s.data.length)
        .filter((s) => !(s.hideWhenZero && (s.data[idx] ?? 0) === 0))
        .map((s) => {
          const raw = s.data[idx] ?? 0;
          return {
            raw,
            row: {
              label: s.label,
              color: s.color,
              value: (s.yAxis === 'right' ? fmtRight : fmtLeft)(raw),
            },
          };
        })
    );
  }

  get tooltipRows() {
    return this.cappedTooltip.rows;
  }

  get tooltipMoreCount() {
    return this.cappedTooltip.moreCount;
  }

  get legendItems() {
    return this.series
      .filter((s) => !s.hidden)
      .map((s) => ({
        label: s.label,
        color: s.color,
        dashed: s.dashed,
      }));
  }

  // ── Canvas modifier ───────────────────────────────────────────────────────

  installCanvas = modifier((element: HTMLElement) => {
    // Access args explicitly so this modifier re-runs when they change
    const _labels = this.labels;
    const _series = this.series;
    const _yMax = this.args.yMax;
    const _yMaxRight = this.args.yMaxRight;
    void _labels;
    void _series;
    void _yMax;
    void _yMaxRight;

    const canvas = element.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) return;

    const observer = createDebouncedResizeObserver((entry) => {
      this.containerWidth = entry.contentRect.width;
      this.draw(canvas);
    });
    observer.observe(element);
    this.containerWidth = element.clientWidth || 400;
    this.draw(canvas);

    const onMove = (e: MouseEvent) => this.handleMouseMove(e, canvas);
    const onLeave = () => {
      this.hoveredIndex = null;
      this.draw(canvas);
    };
    canvas.addEventListener('mousemove', onMove);
    canvas.addEventListener('mouseleave', onLeave);

    return () => {
      observer.disconnect();
      canvas.removeEventListener('mousemove', onMove);
      canvas.removeEventListener('mouseleave', onLeave);
    };
  });

  // ── Mouse handling ────────────────────────────────────────────────────────

  handleMouseMove(e: MouseEvent, canvas: HTMLCanvasElement) {
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const labels = this.labels;
    if (!labels.length) return;

    const shouldRotate = labels.length > 5;
    const bottomMargin = shouldRotate ? 100 : 40;
    const layout = computeLayout(this.containerWidth, this.height, {
      bottom: bottomMargin,
      left: 65,
      right: this.rightMargin,
    });

    const xStep = labels.length > 1 ? layout.innerWidth / (labels.length - 1) : 0;
    const rawIdx = Math.round((mx - layout.left) / (xStep || 1));
    const idx = Math.max(0, Math.min(labels.length - 1, rawIdx));

    if (idx !== this.hoveredIndex) {
      this.hoveredIndex = idx;
      this.tooltipX = rect.left + layout.left + idx * xStep;
      this.tooltipY = e.clientY - 8;
      this.draw(canvas);
    }
  }

  // ── Drawing ───────────────────────────────────────────────────────────────

  draw(canvas: HTMLCanvasElement) {
    const width = this.containerWidth;
    const height = this.height;
    const labels = this.labels;
    const series = this.series;

    if (!labels.length || !series.length) return;

    const shouldRotate = labels.length > 5;
    const bottomMargin = shouldRotate ? 100 : 40;
    const layout = computeLayout(width, height, {
      bottom: bottomMargin,
      left: 65,
      right: this.rightMargin,
    });
    const theme = getThemeColors();

    const ctx = setupCanvas(canvas, width, height);
    ctx.clearRect(0, 0, width, height);

    // ── Scales ───────────────────────────────────────────────────────────────
    const leftSeries = series.filter((s) => (s.yAxis ?? 'left') === 'left');
    const rightSeries = series.filter((s) => s.yAxis === 'right');

    const allLeftValues = leftSeries.flatMap((ser) => ser.data.filter((v) => isFinite(v)));
    const allRightValues = rightSeries.flatMap((ser) => ser.data.filter((v) => isFinite(v)));

    // A caller-supplied yMax/yMaxRight acts as a floor so several charts can be
    // pinned to the same scale; the data still expands the axis if it exceeds it.
    const leftMax = Math.max(
      this.args.yMax ?? 0,
      allLeftValues.length ? Math.max(...allLeftValues, 0) : 0
    );
    const rightMax = Math.max(
      this.args.yMaxRight ?? 0,
      allRightValues.length ? Math.max(...allRightValues, 0) : 0
    );

    const leftTicks = niceAxisTicks(0, leftMax);
    const rightTicks = rightMax > 0 ? niceAxisTicks(0, rightMax) : null;

    const yLeft = linearScale(
      [0, leftTicks[leftTicks.length - 1]!],
      [layout.top + layout.innerHeight, layout.top]
    );
    const yRight =
      rightTicks && rightTicks.length
        ? linearScale(
            [0, rightTicks[rightTicks.length - 1]!],
            [layout.top + layout.innerHeight, layout.top]
          )
        : null;

    const xStep = labels.length > 1 ? layout.innerWidth / (labels.length - 1) : 0;
    const xPositions = labels.map((_, i) => layout.left + i * xStep);
    const baseline = layout.top + layout.innerHeight;

    // ── Grid & axes ───────────────────────────────────────────────────────────
    drawGridLines(ctx, leftTicks, yLeft, layout.left, layout.left + layout.innerWidth, theme.grid);

    const fmtLeft = this.args.formatValue ?? formatAxisValue;
    drawYAxisLabels(ctx, leftTicks, yLeft, layout.left - 8, theme.textMuted, fmtLeft);

    // Right y-axis tick labels
    if (rightTicks && yRight) {
      const fmtRight = this.args.formatValueRight ?? fmtLeft;
      ctx.save();
      ctx.textAlign = 'left';
      ctx.font = '10px system-ui, sans-serif';
      ctx.fillStyle = theme.textMuted;
      for (const tick of rightTicks) {
        ctx.fillText(fmtRight(tick), layout.left + layout.innerWidth + 6, yRight(tick) + 3);
      }
      ctx.restore();
    }

    const maxLabels = Math.max(4, Math.floor(layout.innerWidth / (shouldRotate ? 50 : 80)));
    drawXAxisLabels(
      ctx,
      labels,
      xPositions,
      layout.top + layout.innerHeight + 8,
      theme.textMuted,
      shouldRotate,
      maxLabels
    );

    // ── Series (fills first, then lines on top) ───────────────────────────────
    for (const s of series) {
      if (s.hidden || !s.filled) continue;
      const yScale = s.yAxis === 'right' && yRight ? yRight : yLeft;
      const n = Math.min(s.data.length, labels.length);
      if (!n) continue;

      const pts = Array.from({ length: n }, (_, i) => ({
        x: xPositions[i]!,
        y: yScale(isFinite(s.data[i]!) ? s.data[i]! : 0),
      }));

      ctx.save();
      ctx.globalAlpha = 0.15;
      ctx.fillStyle = s.color;
      ctx.beginPath();
      ctx.moveTo(pts[0]!.x, baseline);
      pts.forEach((p) => ctx.lineTo(p.x, p.y));
      ctx.lineTo(pts[n - 1]!.x, baseline);
      ctx.closePath();
      ctx.fill();
      ctx.restore();
    }

    for (const s of series) {
      if (s.hidden) continue;
      const yScale = s.yAxis === 'right' && yRight ? yRight : yLeft;
      const n = Math.min(s.data.length, labels.length);
      if (!n) continue;

      ctx.save();
      ctx.strokeStyle = s.color;
      ctx.lineWidth = s.dashed ? 1.5 : 2;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      if (s.dashed) ctx.setLineDash([5, 4]);

      ctx.beginPath();
      for (let i = 0; i < n; i++) {
        const x = xPositions[i]!;
        const y = yScale(isFinite(s.data[i]!) ? s.data[i]! : 0);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.restore();
    }

    // ── Hover crosshair & dots ────────────────────────────────────────────────
    if (this.hoveredIndex !== null) {
      const xi = Math.min(this.hoveredIndex, labels.length - 1);
      const x = xPositions[xi]!;

      ctx.save();
      ctx.strokeStyle = theme.grid;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(x, layout.top);
      ctx.lineTo(x, baseline);
      ctx.stroke();
      ctx.restore();

      for (const s of series) {
        if (s.hidden) continue;
        if (xi >= s.data.length || !isFinite(s.data[xi]!)) continue;
        const yScale = s.yAxis === 'right' && yRight ? yRight : yLeft;
        const y = yScale(s.data[xi]!);

        ctx.fillStyle = s.color;
        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();

        ctx.fillStyle = theme.tooltipBg ?? '#fff';
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  // ── Template ──────────────────────────────────────────────────────────────

  <template>
    <div
      data-test-chart
      data-test-chart-type="multi-line"
      class="relative w-full"
      {{this.installCanvas}}
      ...attributes
    >
      <canvas
        {{style width=(concat this.containerWidth "px") height=(concat this.height "px")}}
      ></canvas>

      {{! Hover tooltip — fixed position so it floats above all content }}
      {{#if this.tooltipRows.length}}
        <div
          data-test-multi-line-chart-tooltip
          class="tio-chart-tooltip"
          {{style left=(concat this.tooltipX "px") top=(concat this.tooltipY "px")}}
          {{clampTooltip (concat this.tooltipX ":" this.tooltipY)}}
        >
          <div class="font-medium mb-1 tio-text-muted">{{this.tooltipLabel}}</div>
          {{#each this.tooltipRows as |row|}}
            <div class="tio-row-between gap-3">
              <span class="tio-row-extra-tight">
                <span class="tio-chart-dot-swatch" {{style color=row.color}}></span>
                {{row.label}}
              </span>
              <span class="font-medium">{{row.value}}</span>
            </div>
          {{/each}}
          {{#if this.tooltipMoreCount}}
            <div class="mt-1 text-xs tio-text-muted">+ {{this.tooltipMoreCount}} more</div>
          {{/if}}
        </div>
      {{/if}}

      <div class="tio-wrap-flush gap-x-4 gap-y-1 mt-2 justify-center text-xs tio-fg-muted">
        {{#each this.legendItems as |item|}}
          <span class="tio-row-snug">
            <span
              class="tio-chart-line-swatch {{if item.dashed 'tio-chart-line-swatch--dashed'}}"
              {{style color=item.color}}
            ></span>
            {{item.label}}
          </span>
        {{/each}}
      </div>
    </div>
  </template>
}

export type { MultiLineChartSignature };
